import React from "react";
import {
  Avatar,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  LinearProgress,
  Rating,
  Stack,
  Typography,
  Box,
  Tooltip,
  Tabs,
  Tab,
  Chip,
  IconButton,
  InputAdornment,
  alpha,
  Divider,
  Paper,
} from "@mui/material";
import useDebounce from "../../hooks/useDebounce";
import { useGetTalentsQuery, useSearchResumeMutation, useGetTalentByIdQuery } from "../../services/talent.service";
import { useGetFavoriteTalentsQuery } from "../../services/favoriteTalent.service";
import { Search, Clear, Info, ArrowBack } from "@mui/icons-material";
import {
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
  GridToolbarContainer,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { Talent, User } from "../../types/users";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { Project } from "../../types/project";
import ModalTalentDetail from "./ModalTalentDetail";
import { useInviteTalentMutation } from "../../services/projectJob.service";
import { toast } from "react-toastify";
import { useGetProjectBidsQuery } from "../../services/projectJob.service";
import { useCompany } from "../../hooks/useCompany";
import { filterPrivateTalent } from "../../utils/helper";
import { useUILabel } from "../../hooks/useUILabel";
import { USStates } from "../../components/SelectState";
import { BaseModal, StyledTextField, GlassDataGrid } from "../../components/global";
import { useTheme } from "@mui/material/styles";
import { getDistanceBetweenPoints } from "../../utils/mapHelper";

interface ModalInviteProps {
  open: boolean;
  onClose: () => void;
  project: Project;
  showFav: boolean;
  setShowFav: React.Dispatch<React.SetStateAction<boolean>>;
}

const ModalInvite = ({
  open,
  onClose,
  project,
  showFav,
  setShowFav,
}: ModalInviteProps) => {
  const company = useCompany();
  const theme = useTheme();
  const locationLabel = useUILabel("common.location");
  const isDark = theme.palette.mode === "dark";
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(100);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);
  const [activeTab, setActiveTab] = React.useState(0);

  // Resume search state
  const [resumeSearchQuery, setResumeSearchQuery] = React.useState("");
  const [searchResume, { isLoading: isSearchingResume }] = useSearchResumeMutation();
  const [resumeResults, setResumeResults] = React.useState<Talent[]>([]);
  const [resumeTotal, setResumeTotal] = React.useState(0);

  const [talentModal, setTalentModal] = React.useState<{
    visible: boolean;
    talent: Talent | null;
  }>({
    visible: false,
    talent: null,
  });

  const [selectedTalentId, setSelectedTalentId] = React.useState<number | null>(null);
  // Store the row data when we start fetching, so we can use it as fallback
  const [pendingTalentRow, setPendingTalentRow] = React.useState<any>(null);

  const { data: fetchedTalent, isLoading: isLoadingTalentDetail, isError: isErrorTalentDetail, error: errorTalentDetail } = useGetTalentByIdQuery(selectedTalentId!, {
    skip: !selectedTalentId || selectedTalentId === null,
  });

  // Debug: Log when selectedTalentId changes
  React.useEffect(() => {
    if (selectedTalentId) {
      console.log("Selected talent ID changed, will fetch:", selectedTalentId);
    }
  }, [selectedTalentId]);

  // When talent is fetched, update the modal
  React.useEffect(() => {
    console.log("useEffect for fetchedTalent triggered:", {
      hasFetchedTalent: !!fetchedTalent,
      selectedTalentId: selectedTalentId,
      isLoading: isLoadingTalentDetail,
      fetchedTalentId: fetchedTalent?.id,
      hasPendingRow: !!pendingTalentRow
    });

    if (fetchedTalent && selectedTalentId && !isLoadingTalentDetail) {
      if (fetchedTalent.id) {
        // Validate that fetched talent matches the requested ID
        const pendingRowId = pendingTalentRow?.id || pendingTalentRow?.user_id || pendingTalentRow?.user?.id;
        const fetchedId = fetchedTalent.id;

        // Check if IDs match (fetched ID should match selectedTalentId or pendingRowId)
        const isIdMatch = fetchedId === selectedTalentId ||
          fetchedId === pendingRowId ||
          (pendingTalentRow && fetchedId === pendingTalentRow.user_id);

        if (!isIdMatch && pendingTalentRow) {
          // ID mismatch - use search result data instead to ensure correct talent is shown
          console.warn("ID mismatch detected. Using search result data to ensure correct talent:", {
            requestedId: selectedTalentId,
            pendingRowId: pendingRowId,
            fetchedId: fetchedId,
            pendingRowName: pendingTalentRow.full_name || pendingTalentRow.user?.name,
            fetchedName: fetchedTalent.full_name
          });
          setTalentModal({ visible: true, talent: pendingTalentRow as Talent });
          setPendingTalentRow(null);
          setSelectedTalentId(null);
          return;
        }

        // IDs match - merge fetched data with search result data
        const fetchedTalentAny = fetchedTalent as any;
        let mergedTalent: any = { ...fetchedTalentAny };

        if (pendingTalentRow) {
          // Merge data from search results (especially parsed_data, matching_score, etc.)
          mergedTalent = {
            ...fetchedTalentAny,
            // Preserve search-specific data
            parsed_data: pendingTalentRow.parsed_data || fetchedTalentAny.parsed_data,
            matching_score: pendingTalentRow.matching_score || fetchedTalentAny.matching_score,
            similarity_score: pendingTalentRow.similarity_score || fetchedTalentAny.similarity_score,
            // Preserve user_id if different
            user_id: pendingTalentRow.user_id || fetchedTalentAny.id,
            // Merge user data if available
            user: pendingTalentRow.user || fetchedTalentAny.user,
            // Use fetched data for complete fields, but keep search data if fetched is missing
            full_name: fetchedTalentAny.full_name || pendingTalentRow.full_name,
            first_name: fetchedTalentAny.first_name || pendingTalentRow.first_name,
            last_name: fetchedTalentAny.last_name || pendingTalentRow.last_name,
            email: fetchedTalentAny.email || pendingTalentRow.email,
            phone_number: fetchedTalentAny.phone_number || pendingTalentRow.phone_number,
            photo: fetchedTalentAny.photo?.length > 0 ? fetchedTalentAny.photo : (pendingTalentRow.photo || []),
            // Keep skills from fetched (more complete), but fallback to search if needed
            skills: fetchedTalentAny.skills?.length > 0 ? fetchedTalentAny.skills : (pendingTalentRow.skills || pendingTalentRow.user?.skills || []),
            business_verticals: fetchedTalentAny.business_verticals?.length > 0 ? fetchedTalentAny.business_verticals : (pendingTalentRow.business_verticals || pendingTalentRow.user?.business_verticals || []),
          };
        }

        console.log("Opening talent modal with merged data (fetched + search results):", {
          name: mergedTalent.full_name,
          id: mergedTalent.id,
          requestedId: selectedTalentId
        });
        setTalentModal({ visible: true, talent: mergedTalent as Talent });
        setPendingTalentRow(null); // Clear fallback data
        setSelectedTalentId(null); // Reset after use
      } else {
        console.warn("Fetched talent data is invalid, using search result data:", {
          fetchedTalent,
          selectedTalentId,
          fetchedId: fetchedTalent?.id
        });
        // Use fallback data if available
        if (pendingTalentRow) {
          console.warn("Using search result data due to invalid fetched data");
          setTalentModal({ visible: true, talent: pendingTalentRow as Talent });
          setPendingTalentRow(null);
        } else {
          toast.error("Invalid talent data received");
        }
        setSelectedTalentId(null);
      }
    }
  }, [fetchedTalent, selectedTalentId, isLoadingTalentDetail, pendingTalentRow]);

  // redeploy to staging
  // Handle error when fetching talent details - use fallback data if available
  React.useEffect(() => {
    if (isErrorTalentDetail && selectedTalentId) {
      let errorMessage = "Failed to load full talent details";
      if (errorTalentDetail) {
        if ('data' in errorTalentDetail && errorTalentDetail.data && typeof errorTalentDetail.data === 'object' && 'message' in errorTalentDetail.data) {
          errorMessage = String(errorTalentDetail.data.message);
        } else if ('message' in errorTalentDetail) {
          errorMessage = String(errorTalentDetail.message);
        }
      }

      // If we have fallback data, use it (even if incomplete)
      // Check by user_id or id match since they might differ
      if (pendingTalentRow) {
        const rowId = pendingTalentRow.id || pendingTalentRow.user_id || pendingTalentRow.user?.id;
        if (rowId === selectedTalentId || pendingTalentRow.user_id === selectedTalentId) {
          console.warn("Using fallback data for talent modal due to fetch error:", pendingTalentRow);
          toast.warning("Showing available data (some details may be incomplete)");
          setTalentModal({ visible: true, talent: pendingTalentRow });
          setPendingTalentRow(null);
        } else {
          // Still use it if it's the only data we have
          console.warn("Using fallback data despite ID mismatch:", { rowId, selectedTalentId });
          setTalentModal({ visible: true, talent: pendingTalentRow });
          setPendingTalentRow(null);
        }
      } else {
        toast.error(errorMessage);
      }
      setSelectedTalentId(null); // Reset on error
    }
  }, [isErrorTalentDetail, errorTalentDetail, selectedTalentId, pendingTalentRow]);

  const [resumeDetailModal, setResumeDetailModal] = React.useState<{
    visible: boolean;
    data: any | null;
  }>({
    visible: false,
    data: null,
  });

  const { data: bids } = useGetProjectBidsQuery(project.id, {
    skip: !open,
  });


  const {
    data: dataTalent,
    isLoading: isLoadingTalent,
    isFetching: isFetchingTalent,
  } = useGetTalentsQuery(
    {
      page,
      pageSize,
      sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
      sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
      search: searchQuery,
      load: "user,user.photo,photo,talent,business_verticals,skills",
      // Only send skills filter if project has skills, otherwise undefined (no filter)
      skills: project.skills && project.skills.length > 0 ? project.skills.map((it) => it.id) : undefined,
      state:
        (USStates.find((it) => it.label == project.location?.state)?.value as string) ||
        project.location?.state,
      // city: project.location?.city,
    },
    {
      skip: !open,
    }
  );
  const { data: favTalents } = useGetFavoriteTalentsQuery(
    {
      page: 0,
      pageSize: 1000,
    },
    {
      skip: !open,
    }
  );
  const [doInvite, { isLoading: isInviting }] = useInviteTalentMutation();

  const rowTalents: Talent[] | undefined = React.useMemo(() => {
    let data = dataTalent?.data;
    if (!!showFav) {
      data = data?.filter(
        (it) => !!favTalents?.data.find((x) => x.user.id == it.id)
      );
    }
    const sorted = data?.slice().sort((a: User, b: User) => {
      if (
        a.city === project.location?.city &&
        b.city !== project.location?.city
      ) {
        return -1;
      } else if (
        a.city !== project.location?.city &&
        b.city === project.location?.city
      ) {
        return 1;
      } else {
        // For other cities or if both are project city, maintain original order
        return 0;
      }
    });
    return filterPrivateTalent(sorted, company);
  }, [dataTalent, favTalents, showFav, project]);

  const handleInvite = React.useCallback(
    async (resource_id: number) => {
      try {
        await doInvite({ project_id: project.id, resource_id }).unwrap();
        toast.success("Invitation sent successfully");
      } catch (err: any) {
        toast.error(err?.data?.message || err?.message || "Failed to send invitation");
      }
    },
    [project, doInvite]
  );

  const columnTalents: GridColDef[] = React.useMemo(
    () => [
      {
        field: "first_name",
        headerName: "Independent Professional",
        flex: 1,
        sortable: true,
        filterable: true,
        renderCell: (params: GridRenderCellParams) => (
          <Stack direction={"row"}>
            <Avatar
              alt={`${params.row.first_name}`}
              src={`${params.row?.photo?.[0]?.original_url}`}
            />
            <Stack
              sx={{ ml: 1 }}
              direction={"column"}
              justifyContent={"center"}
            >
              <Typography
                variant="body1"
                onClick={(e) => {
                  e.stopPropagation();
                  // Always fetch full talent data using the same API for consistency
                  // This ensures we get complete data whether from Browse Talents or AI Search

                  // For AI search results, prefer user_id or user.id (these are the actual talent/user IDs)
                  // For regular browse talents, use id directly
                  const isAiSearchResult = !!(params.row.parsed_data || (params.row.user && params.row.user.skills));
                  const talentId = isAiSearchResult
                    ? (params.row.user_id || params.row.user?.id || params.row.id)
                    : (params.row.id || params.row.user_id || params.row.user?.id);
                  const talentName = params.row.full_name || params.row.user?.name || "Unknown";

                  if (talentId) {
                    const numericId = Number(talentId);
                    console.log("Fetching full talent data:", {
                      talentId: numericId,
                      talentName: talentName,
                      rowId: params.row.id,
                      userId: params.row.user_id,
                      userObjId: params.row.user?.id,
                      isAiSearch: isAiSearchResult,
                      rawTalentId: talentId,
                      convertedToNumber: numericId,
                      fullRow: params.row
                    });
                    // Store row data to merge with fetched data later (for AI search specific data like parsed_data, matching_score)
                    setPendingTalentRow(params.row);
                    // Ensure we set a valid number
                    if (!isNaN(numericId) && numericId > 0) {
                      console.log("Setting selectedTalentId to:", numericId);
                      setSelectedTalentId(numericId);
                    } else {
                      console.error("Invalid talent ID:", talentId, "Using fallback data");
                      setTalentModal({ visible: true, talent: params.row });
                    }
                  } else {
                    console.warn("No ID found, using available data:", {
                      row: params.row,
                      talentName: talentName,
                      isAiSearch: isAiSearchResult
                    });
                    // Fallback: use available data if no ID
                    setTalentModal({ visible: true, talent: params.row });
                  }
                }}
                sx={{
                  cursor: "pointer",
                  color: "primary.main",
                  fontWeight: 500,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {params.row.full_name}
              </Typography>
              {favTalents?.data?.find((it) => it.user.id == params.row.id) !=
                undefined && (
                  <Typography variant="body2">♥️ Favorited</Typography>
                )}
            </Stack>
          </Stack>
        ),
      },
      {
        field: "contact",
        headerName: "Phone / Email",
        minWidth: 250,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                📞
              </Typography>
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                {params.row.phone_number || "N/A"}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                ✉️
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "primary.main",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
                component="a"
                href={`mailto:${params.row.email}`}
              >
                {params.row.email || "N/A"}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        field: "rating",
        headerName: "Rating",
        minWidth: 150,
        sortable: true,
        filterable: true,
        renderCell: (params: GridRenderCellParams) => (
          <Rating value={params.row.total_rating} readOnly precision={0.5} />
        ),
      },
      {
        field: "skills",
        headerName: "Matched Skills",
        minWidth: 120,
        sortable: false,
        filterable: false,
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          // For resume search results, show parsed_data skills
          if (params.row.parsed_data?.skills) {
            const parsedSkills = params.row.parsed_data.skills || [];
            const projectSkills = project.skills || [];

            // If project has no skills, show parsed skills count
            if (!projectSkills || projectSkills.length === 0) {
              return (
                <Chip
                  label={`${parsedSkills.length} skills`}
                  size="small"
                  sx={{
                    background: alpha("#666666", 0.1),
                    color: "text.secondary",
                    fontWeight: 600,
                  }}
                />
              );
            }

            // Normalize skill name for better matching (remove dots, dashes, spaces, etc)
            const normalizeSkillName = (name: string): string => {
              return name
                .toLowerCase()
                .replace(/[\.\-\s]/g, "") // Remove dots, dashes, spaces
                .trim();
            };

            // Extract base name (remove extensions like .js, .ts, etc)
            const getBaseName = (name: string): string => {
              const lower = name.toLowerCase().trim();
              // Remove common extensions
              return lower.replace(/\.(js|ts|jsx|tsx|vue|py|java|php|rb|go|rs)$/, "");
            };

            // Match parsed skills with project skills by name (case-insensitive, partial match)
            const matchedProjectSkills = projectSkills.filter((projectSkill: any) => {
              if (!projectSkill || !projectSkill.name) return false;
              const projectLower = projectSkill.name.toLowerCase().trim();
              const projectNormalized = normalizeSkillName(projectSkill.name);
              const projectBase = getBaseName(projectSkill.name);

              return parsedSkills.some((parsedSkill: string) => {
                const parsedLower = parsedSkill.toLowerCase().trim();
                const parsedNormalized = normalizeSkillName(parsedSkill);
                const parsedBase = getBaseName(parsedSkill);

                // Try exact match first (normalized)
                if (parsedNormalized === projectNormalized) return true;

                // Try case-insensitive exact match
                if (parsedLower === projectLower) return true;

                // Try base name matching (e.g., "React.js" matches "React")
                if (parsedBase === projectBase && parsedBase.length >= 2) return true;
                if (parsedBase.length >= 3 && projectBase.length >= 3) {
                  if (parsedBase.includes(projectBase) || projectBase.includes(parsedBase)) return true;
                }

                // Try partial match (one contains the other)
                if (parsedLower.length >= 3 && projectLower.length >= 3) {
                  if (parsedLower.includes(projectLower) || projectLower.includes(parsedLower)) return true;
                }

                // Try word-by-word matching for multi-word skills
                if (parsedSkill.includes(" ")) {
                  const parsedWords = parsedSkill.toLowerCase().split(/\s+/);
                  const significantWords = parsedWords.filter((word: string) => word.length >= 3);
                  if (significantWords.length > 0 && significantWords.every((word: string) => projectLower.includes(word))) return true;
                }

                return false;
              });
            });

            return (
              <Tooltip
                arrow
                title={
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                      Matched with Project: {matchedProjectSkills.map((it: any) => it.name).join(", ") || "None"}
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block" }}>
                      All Parsed Skills: {parsedSkills.slice(0, 10).join(", ")}
                      {parsedSkills.length > 10 && ` +${parsedSkills.length - 10} more`}
                    </Typography>
                  </Box>
                }
              >
                <Typography variant="button">
                  {matchedProjectSkills.length || 0} / {parsedSkills.length}
                </Typography>
              </Tooltip>
            );
          }
          // For resume search results without parsed_data, show user.skills
          if (!params.row.parsed_data && params.row.user?.skills && params.row.user.skills.length > 0) {
            const userSkills = params.row.user.skills || [];
            const projectSkills = project.skills || [];

            // If project has no skills, show user skills count
            if (!projectSkills || projectSkills.length === 0) {
              return (
                <Chip
                  label={`${userSkills.length} skills`}
                  size="small"
                  sx={{
                    background: alpha("#666666", 0.1),
                    color: "text.secondary",
                    fontWeight: 600,
                  }}
                />
              );
            }

            // Normalize skill name for better matching (remove dots, dashes, spaces, etc)
            const normalizeSkillName = (name: string): string => {
              return name
                .toLowerCase()
                .replace(/[\.\-\s]/g, "") // Remove dots, dashes, spaces
                .trim();
            };

            // Extract base name (remove extensions like .js, .ts, etc)
            const getBaseName = (name: string): string => {
              const lower = name.toLowerCase().trim();
              // Remove common extensions
              return lower.replace(/\.(js|ts|jsx|tsx|vue|py|java|php|rb|go|rs)$/, "");
            };

            // Match user skills with project skills by name (case-insensitive, partial match)
            const matchedProjectSkills = userSkills.filter((userSkill: any) => {
              if (!userSkill || !userSkill.name) return false;
              const userSkillName = userSkill.name;
              const userLower = userSkillName.toLowerCase().trim();
              const userNormalized = normalizeSkillName(userSkillName);
              const userBase = getBaseName(userSkillName);

              return projectSkills.some((projectSkill: any) => {
                if (!projectSkill || !projectSkill.name) return false;

                // First try ID match (exact match)
                if (projectSkill.id === userSkill.id) return true;

                const projectLower = projectSkill.name.toLowerCase().trim();
                const projectNormalized = normalizeSkillName(projectSkill.name);
                const projectBase = getBaseName(projectSkill.name);

                // Try exact match first (normalized)
                if (userNormalized === projectNormalized) return true;

                // Try case-insensitive exact match
                if (userLower === projectLower) return true;

                // Try base name matching (e.g., "React.js" matches "React")
                if (userBase === projectBase && userBase.length >= 2) return true;
                if (userBase.length >= 3 && projectBase.length >= 3) {
                  if (userBase.includes(projectBase) || projectBase.includes(userBase)) return true;
                }

                // Try partial match (one contains the other)
                if (userLower.length >= 3 && projectLower.length >= 3) {
                  if (userLower.includes(projectLower) || projectLower.includes(userLower)) return true;
                }

                // Try word-by-word matching for multi-word skills
                if (userSkillName.includes(" ")) {
                  const userWords = userSkillName.toLowerCase().split(/\s+/);
                  const significantWords = userWords.filter((word: string) => word.length >= 3);
                  if (significantWords.length > 0 && significantWords.every((word: string) => projectLower.includes(word))) return true;
                }

                return false;
              });
            });

            return (
              <Tooltip
                arrow
                title={
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                      Matched with Project: {matchedProjectSkills.map((it: any) => it.name).join(", ") || "None"}
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block" }}>
                      All Skills: {userSkills.map((it: any) => it.name).slice(0, 10).join(", ")}
                      {userSkills.length > 10 && ` +${userSkills.length - 10} more`}
                    </Typography>
                  </Box>
                }
              >
                <Typography variant="button">
                  {matchedProjectSkills.length || 0} / {userSkills.length}
                </Typography>
              </Tooltip>
            );
          }
          // For regular talent search
          const matched = project.skills?.filter(
            (it) => params.row.skills?.find((s: any) => s.id == it.id) != undefined
          );
          return (
            <Tooltip arrow title={matched?.map((it) => it.name).join(", ") || "No matches"}>
              <Typography variant="button">{matched?.length || 0}</Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "similarity_score",
        headerName: "Match Score",
        minWidth: 120,
        sortable: true,
        filterable: false,
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          // Support both new matching_score object and old similarity_score
          const totalScore = params.row.matching_score?.total_score ?? params.row.similarity_score;
          if (totalScore != null) {
            const score = Number(totalScore);
            const percentage = (score * 100).toFixed(1);
            return (
              <Tooltip
                arrow
                title={
                  params.row.matching_score ? (
                    <Box>
                      <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                        <strong>Total Score:</strong> {((params.row.matching_score.total_score || 0) * 100).toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                        <strong>Similarity:</strong> {((params.row.matching_score.similarity_score || 0) * 100).toFixed(1)}%
                      </Typography>
                      {params.row.matching_score.skill_match_score != null && (
                        <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                          <strong>Skill Match:</strong> {(params.row.matching_score.skill_match_score * 100).toFixed(1)}%
                        </Typography>
                      )}
                      {params.row.matching_score.years_experience_score != null && (
                        <Typography variant="caption" sx={{ display: "block" }}>
                          <strong>Experience:</strong> {(params.row.matching_score.years_experience_score * 100).toFixed(1)}%
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    `Match Score: ${percentage}%`
                  )
                }
              >
                <Chip
                  label={`${percentage}%`}
                  color={score >= 0.7 ? "success" : score >= 0.5 ? "warning" : "default"}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Tooltip>
            );
          }
          return <Typography variant="body2">-</Typography>;
        },
        valueGetter: (params: GridValueGetterParams) => {
          if (!params || !params.row) return 0;
          return params.row.matching_score?.total_score ?? params.row.similarity_score ?? 0;
        },
      },
      {
        field: "min_pay",
        headerName: "Rate ($)",
        minWidth: 100,
        sortable: true,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as Talent;
          const minPay = row?.min_pay;
          const maxPay = row?.max_pay;

          if (minPay != null && maxPay != null) {
            return (
              <Typography variant="body2">
                ${Number(minPay).toLocaleString()} - ${Number(maxPay).toLocaleString()}/hr
              </Typography>
            );
          } else if (minPay != null) {
            return (
              <Typography variant="body2">
                ${Number(minPay).toLocaleString()}/hr
              </Typography>
            );
          } else if (maxPay != null) {
            return (
              <Typography variant="body2">
                ${Number(maxPay).toLocaleString()}/hr
              </Typography>
            );
          } else {
            return (
              <Typography variant="body2" color="text.secondary">
                N/A
              </Typography>
            );
          }
        },
        valueGetter: (params: GridValueGetterParams) => {
          if (!params || !params.row) {
            return 0;
          }
          const row = params.row as Talent;
          const minPay = row?.min_pay;
          const maxPay = row?.max_pay;

          if (minPay != null && maxPay != null) {
            return Number(minPay) + Number(maxPay); // For sorting purposes
          } else if (minPay != null) {
            return Number(minPay);
          } else if (maxPay != null) {
            return Number(maxPay);
          }
          return 0;
        },
      },
      {
        field: "distance",
        headerName: "📍 Distance to Site",
        minWidth: 160,
        sortable: true,
        filterable: false,
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          // Support distance from matching_score (AI search) or calculate from coordinates
          const distanceKm = params.row.matching_score?.distance_km;
          const distanceCalculation = params.row.matching_score?.distance_calculation;

          if (distanceKm != null) {
            const distanceMiles = Number(distanceKm) * 0.621371; // Convert km to miles
            // Color code by distance: green for close (< 50 miles), yellow for medium (50-200), red for far (> 200)
            const color = distanceMiles < 50 ? "#12903C" : distanceMiles < 200 ? "#ff9800" : "#f44336";
            return (
              <Tooltip
                arrow
                title={
                  distanceCalculation?.note
                    ? distanceCalculation.note
                    : `${distanceMiles.toFixed(1)} miles from project location`
                }
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: color,
                  }}
                >
                  {distanceMiles.toFixed(1)} miles
                </Typography>
              </Tooltip>
            );
          }
          // Fallback: calculate from coordinates if available
          if (params.row.coordinate && project.location?.coordinate) {
            const dist = getDistanceBetweenPoints(
              {
                lat: project.location.coordinate.coordinates[1] || 0,
                lng: project.location.coordinate.coordinates[0] || 0,
              },
              {
                lat: params.row.coordinate.coordinates[1] || 0,
                lng: params.row.coordinate.coordinates[0] || 0,
              }
            );
            const distanceMiles = Number(dist);
            const color = distanceMiles < 50 ? "#12903C" : distanceMiles < 200 ? "#ff9800" : "#f44336";
            return (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: color,
                }}
              >
                {distanceMiles.toFixed(1)} miles
              </Typography>
            );
          }
          // Show helpful message if distance cannot be calculated
          return (
            <Tooltip
              arrow
              title={
                distanceCalculation?.note ||
                "Distance requires both project and user coordinates. User can set coordinates in their profile."
              }
            >
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                N/A
              </Typography>
            </Tooltip>
          );
        },
        valueGetter: (params: GridValueGetterParams) => {
          // For sorting purposes
          if (!params || !params.row) return 999999;
          const distanceKm = params.row.matching_score?.distance_km;
          if (distanceKm != null) {
            return Number(distanceKm) * 0.621371; // Convert to miles for sorting
          }
          if (params.row.coordinate && project.location?.coordinate) {
            return getDistanceBetweenPoints(
              {
                lat: project.location.coordinate.coordinates[1] || 0,
                lng: project.location.coordinate.coordinates[0] || 0,
              },
              {
                lat: params.row.coordinate.coordinates[1] || 0,
                lng: params.row.coordinate.coordinates[0] || 0,
              }
            );
          }
          return 999999; // Put N/A at the end when sorting
        },
      },
      {
        field: "city",
        headerName: "City",
        minWidth: 150,
        sortable: true,
        filterable: true,
      },
      {
        field: "state",
        headerName: "State",
        minWidth: 100,
        sortable: true,
        filterable: true,
      },
      {
        field: "actions",
        headerName: "Action",
        minWidth: 250,
        renderCell: (params: GridRenderCellParams) => {
          const alreadyBid = bids?.find(
            (it) => it.resource_id == params.row.id
          );

          if (alreadyBid) {
            return (
              <Typography fontStyle={"italic"} variant="body2">
                Invited
              </Typography>
            );
          }

          // Only show Details button for AI search results (has parsed_data or user.skills structure)
          const isAiSearchResult = params.row.parsed_data || (params.row.user && params.row.user.skills);

          return (
            <Stack direction="row" spacing={1}>
              {isAiSearchResult && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Info />}
                  onClick={() => setResumeDetailModal({ visible: true, data: params.row })}
                  sx={{ textTransform: "none" }}
                >
                  Details
                </Button>
              )}
              <Button
                variant="contained"
                color="info"
                size="small"
                onClick={() => handleInvite(params.row.id)}
                sx={{ textTransform: "none" }}
              >
                Invite
              </Button>
            </Stack>
          );
        },
      },
    ],
    [bids, project, favTalents, handleInvite, setTalentModal, setResumeDetailModal]
  );

  // Columns for Browse Talents (without Match Score)
  const columnTalentsForBrowse: GridColDef[] = React.useMemo(
    () => columnTalents.filter(col => col.field !== 'similarity_score'),
    [columnTalents]
  );

  // Columns for AI Search (without Rate column)
  const columnTalentsForAiSearch: GridColDef[] = React.useMemo(
    () => columnTalents.filter(col => col.field !== 'min_pay'),
    [columnTalents]
  );

  React.useEffect(() => {
    setSearchQuery(debounceQuery);
  }, [debounceQuery]);

  const TableToolbar = ({
    onChangeFav,
  }: {
    onChangeFav: (event: React.ChangeEvent<HTMLInputElement>) => void;
  }) => (
    <GridToolbarContainer
      style={{ paddingLeft: 10, paddingRight: 10, marginBottom: 5 }}
    >
      <StyledTextField
        margin="dense"
        label="Enter to search.."
        value={query || ""}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ minWidth: 250 }}
      />
      {!!query && (
        <Button variant="text" sx={{ mt: 1 }} onClick={() => setQuery("")}>
          Reset
        </Button>
      )}
      <Stack
        spacing={1}
        style={{ flex: 1 }}
        direction="row"
        justifyContent="flex-end"
      >
        <FormGroup>
          <FormControlLabel
            control={<Checkbox onChange={onChangeFav} />}
            label="Only Favorite Independent Professionals"
          />
        </FormGroup>
      </Stack>
    </GridToolbarContainer>
  );

  const handleChangeFav = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setShowFav(event.target.checked);
    },
    [setShowFav]
  );

  const handleResumeSearch = React.useCallback(async () => {
    if (!resumeSearchQuery.trim()) {
      toast.error("Please enter a search query");
      return;
    }
    try {
      // Extract required skill IDs from project requirements
      const requiredSkillIds = project.skills && project.skills.length > 0
        ? project.skills.map((skill) => skill.id)
        : undefined;

      const result = await searchResume({
        query: resumeSearchQuery.trim() || undefined,
        required_skill_ids: requiredSkillIds, // Send project requirements for accurate skill match score
        project_id: project.id, // Send project ID for distance calculation
        limit: 100,
      }).unwrap();
      // Transform data to ensure each row has an id property
      const transformedResults = (result.results || []).map((item: any) => {
        // Use user_id or user.id as id, and ensure the data structure matches Talent type
        const id = item.user_id || item.user?.id || item.id;
        if (!id) {
          console.warn("Resume search result missing id:", item);
        }

        // Extract user data
        const userData = item.user || {};
        const nameParts = (userData.name || '').split(' ');
        const firstName = userData.first_name || nameParts[0] || '';
        const lastName = userData.last_name || nameParts.slice(1).join(' ') || '';
        const fullName = userData.name || userData.full_name || `${firstName} ${lastName}`.trim() || 'Unknown';

        return {
          ...item,
          id: id, // Ensure id is always present
          // Map user data to top level to match Talent structure
          first_name: firstName,
          last_name: lastName,
          full_name: fullName,
          email: userData.email || item.email || '',
          phone_number: userData.phone_number || item.phone_number || '',
          photo: userData.photo || item.photo || [],
          // Map location data
          city: userData.city || item.city || '',
          state: userData.state || item.state || '',
          // Map rating
          total_rating: userData.total_rating || item.total_rating || 0,
          // Preserve user object for skills access when parsed_data is null
          user: item.user || userData,
          // Preserve parsed_data and other fields (resume_url removed - customer cannot access resume files)
          parsed_data: item.parsed_data,
          matching_score: item.matching_score, // New structure with total_score, similarity_score, years_experience_score
          similarity_score: item.similarity_score || item.matching_score?.similarity_score, // Backward compatibility
        };
      });
      setResumeResults(transformedResults);
      setResumeTotal(result.total || 0);
      if (result.total === 0) {
        toast.info(result.message || "No candidates found");
      } else {
        toast.success(`Found ${result.total} candidate(s)`);
      }
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || "Failed to search candidates");
      setResumeResults([]);
      setResumeTotal(0);
    }
  }, [resumeSearchQuery, searchResume, project]);

  const handleClearResumeSearch = React.useCallback(() => {
    setResumeSearchQuery("");
    setResumeResults([]);
    setResumeTotal(0);
  }, []);

  // Dynamic placeholder based on project
  const chatboxPlaceholder = React.useMemo(() => {
    const projectTitle = project?.title || "candidates";
    const firstSkill = project?.skills && project.skills.length > 0
      ? project.skills[0].name
      : null;

    if (firstSkill) {
      return `Ask me to find candidates... (e.g., 'Find ${projectTitle} with ${firstSkill} experience')`;
    }
    return `Ask me to find candidates... (e.g., 'Find ${projectTitle}')`;
  }, [project]);

  const resumeRowTalents: Talent[] | undefined = React.useMemo(() => {
    if (resumeResults.length === 0) return [];
    return filterPrivateTalent(resumeResults, company);
  }, [resumeResults, company]);

  const CustomToolbar = React.useMemo(
    () => () => <TableToolbar onChangeFav={handleChangeFav} />,
    [handleChangeFav]
  );

  const footer = (
    <Button onClick={onClose} variant="contained">
      Close
    </Button>
  );

  return (
    <>
      <BaseModal
        open={open}
        onClose={onClose}
        title="Invite Independent Professional"
        footer={footer}
        maxWidth="xl"
        fullWidth={true}
        useGlassCard={true}
      >
        <Box sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => {
              setActiveTab(newValue);
              if (newValue === 0) {
                handleClearResumeSearch();
              }
            }}
            sx={{
              borderBottom: `1px solid ${isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"}`,
              px: 3,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                minHeight: 48,
              },
            }}
          >
            <Tab label="Browse Talents" />
            <Tab label="Search Candidates (AI)" />
          </Tabs>

          {activeTab === 0 && (
            <Box sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: 0,
            }}>
              <GlassDataGrid
                height="70vh"
                rows={rowTalents || []}
                columns={columnTalentsForBrowse}
                paginationMode="server"
                page={page as any}
                onPageChange={(newPage: any) => setPage(newPage)}
                pageSize={pageSize as any}
                rowsPerPageOptions={[1000]}
                onPageSizeChange={(newSize: any) => setPageSize(newSize)}
                sortModel={sortModel}
                sortingMode="server"
                onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
                loading={isLoadingTalent || isFetchingTalent || isInviting}
                components={{
                  LoadingOverlay: LinearProgress,
                  NoResultsOverlay: NoResultsOverlay,
                  Toolbar: CustomToolbar,
                }}
                disableSelectionOnClick
                disableColumnFilter
                rowHeight={72}
                density="compact"
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: isDark
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(255, 255, 255, 0.5)",
                    backdropFilter: "blur(15px) saturate(180%)",
                    WebkitBackdropFilter: "blur(15px) saturate(180%)",
                    borderBottom: `2px solid ${isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(46, 125, 60, 0.3)"}`,
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      color: "#12903C",
                    },
                  },
                }}
              />
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{
              p: 0,
              display: "flex",
              flexDirection: "column",
              height: "70vh",
              minHeight: "70vh",
              maxHeight: "70vh",
              overflow: "hidden",
              marginBottom: 0,
            }}>
              {/* Show Results Only if there are results */}
              {resumeResults.length > 0 ? (
                <Box sx={{
                  flex: 1,
                  overflowY: "auto",
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                }}>
                  {/* Back Button */}
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBack />}
                    onClick={handleClearResumeSearch}
                    sx={{
                      mb: 2,
                      alignSelf: "flex-start",
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: "#12903C",
                      color: "#12903C",
                      "&:hover": {
                        borderColor: "#0d6e2f",
                        background: alpha("#12903C", 0.1),
                      },
                    }}
                  >
                    Back to Search
                  </Button>

                  {/* Results Grid */}
                  <Box sx={{ width: "100%", flex: 1 }}>
                    <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography variant="h6" sx={{ color: "#12903C", fontWeight: 700 }}>
                        Search Results ({resumeTotal})
                      </Typography>
                      <Chip
                        label={`${resumeTotal} candidate${resumeTotal !== 1 ? 's' : ''} found`}
                        color="success"
                        sx={{
                          fontWeight: 600,
                          borderRadius: "16px",
                        }}
                      />
                    </Box>
                    <GlassDataGrid
                      height="calc(100vh - 350px)"
                      rows={resumeRowTalents}
                      columns={columnTalentsForAiSearch}
                      loading={isInviting}
                      getRowId={(row: any) => row.id || row.user_id || row.user?.id || `resume-${row.user_id || row.user?.id || Math.random()}`}
                      components={{
                        LoadingOverlay: LinearProgress,
                        NoResultsOverlay: NoResultsOverlay,
                      }}
                      disableSelectionOnClick
                      disableColumnFilter
                      disablePagination
                      rowHeight={72}
                      density="compact"
                      sx={{
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: isDark
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(255, 255, 255, 0.5)",
                          backdropFilter: "blur(15px) saturate(180%)",
                          WebkitBackdropFilter: "blur(15px) saturate(180%)",
                          borderBottom: `2px solid ${isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(46, 125, 60, 0.3)"}`,
                          "& .MuiDataGrid-columnHeaderTitle": {
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.8px",
                            color: "#12903C",
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden", position: "relative" }}>
                  {/* Loading Overlay - Full Screen */}
                  {isSearchingResume && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1000,
                        background: isDark
                          ? `linear-gradient(135deg, ${alpha("#000000", 0.95)} 0%, ${alpha("#000000", 0.98)} 100%)`
                          : `linear-gradient(135deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#ffffff", 0.95)} 100%)`,
                        backdropFilter: "blur(20px)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "inset 0 0 100px rgba(18, 144, 60, 0.1)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          background: `linear-gradient(135deg, #12903C 0%, #0d6e2f 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 3,
                          boxShadow: "0 8px 32px rgba(18, 144, 60, 0.4)",
                          animation: "pulse 2s ease-in-out infinite",
                          "@keyframes pulse": {
                            "0%, 100%": {
                              transform: "scale(1)",
                              opacity: 1,
                            },
                            "50%": {
                              transform: "scale(1.1)",
                              opacity: 0.8,
                            },
                          },
                        }}
                      >
                        <Search sx={{ fontSize: 40, color: "#ffffff" }} />
                      </Box>
                      <Typography variant="h5" sx={{ mb: 1, color: "#12903C", fontWeight: 700 }}>
                        Searching for candidates...
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: "center", maxWidth: "400px" }}>
                        Please wait while we find the best matches for your project
                      </Typography>
                      <LinearProgress
                        sx={{
                          width: "300px",
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: isDark ? alpha("#ffffff", 0.1) : alpha("#000000", 0.1),
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 4,
                            background: `linear-gradient(90deg, #12903C 0%, #0d6e2f 100%)`,
                          },
                        }}
                      />
                    </Box>
                  )}

                  {/* Job Requirements Section - No Scroll */}
                  <Box
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      borderBottom: `1px solid ${isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"}`,
                      background: isDark
                        ? `linear-gradient(135deg, ${alpha("#12903C", 0.18)} 0%, ${alpha("#12903C", 0.1)} 100%)`
                        : `linear-gradient(135deg, ${alpha("#12903C", 0.12)} 0%, ${alpha("#12903C", 0.06)} 100%)`,
                      backdropFilter: "blur(20px)",
                      flex: "0 0 auto",
                      overflow: "visible",
                      boxShadow: isDark
                        ? "inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                        : "inset 0 1px 0 rgba(18, 144, 60, 0.1)",
                    }}
                  >
                    <Stack spacing={1.5}>
                      {/* Header */}
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 0.5,
                            color: "#12903C",
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            fontSize: { xs: "1rem", sm: "1.1rem" }
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 28,
                              height: 28,
                              borderRadius: "6px",
                              background: alpha("#12903C", 0.15),
                            }}
                          >
                            <Info sx={{ fontSize: { xs: 16, sm: 18 }, color: "#12903C" }} />
                          </Box>
                          Job Requirements
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.8rem",
                            lineHeight: 1.5,
                            ml: 4.5,
                            mt: -0.25,
                            p: 1,
                            borderRadius: 1,
                            color: "#12903C",
                            fontWeight: 600,
                          }}
                        >
                          Use the search below to find candidates matching these requirements
                        </Typography>
                      </Box>

                      {/* Project Title */}
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            mb: 0.75,
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.8px",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <Box
                            sx={{
                              width: 4,
                              height: 4,
                              borderRadius: "50%",
                              background: "#12903C",
                            }}
                          />
                          Project
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color: "#12903C",
                            fontWeight: 700,
                            fontSize: { xs: "1rem", sm: "1.1rem" },
                            lineHeight: 1.3,
                            ml: 1.75,
                          }}
                        >
                          {project.title}
                        </Typography>
                      </Box>

                      {/* Required Skills */}
                      {project.skills && project.skills.length > 0 && (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              color: "text.secondary",
                              fontSize: "0.65rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px"
                            }}
                          >
                            Required Skills ({project.skills.length})
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                            {project.skills.map((skill) => (
                              <Chip
                                key={skill.id}
                                label={skill.name}
                                size="small"
                                sx={{
                                  background: "#12903C",
                                  color: "#ffffff",
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  height: "24px",
                                  px: 1,
                                  boxShadow: "0 2px 8px rgba(18, 144, 60, 0.2)",
                                  "&:hover": {
                                    background: "#0d6e2f",
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 4px 12px rgba(18, 144, 60, 0.3)",
                                  },
                                  transition: "all 0.2s ease",
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {/* Certifications */}
                      {project.certifications && project.certifications.length > 0 && (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              mb: 1,
                              color: "text.secondary",
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px"
                            }}
                          >
                            Required Certifications ({project.certifications.length})
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                            {project.certifications.map((cert) => (
                              <Chip
                                key={cert.id}
                                label={cert.name}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: "#12903C",
                                  color: "#12903C",
                                  fontWeight: 500,
                                  fontSize: "0.75rem",
                                  height: "26px",
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {/* Location */}
                      {project.location && (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              mb: 0.75,
                              color: "text.secondary",
                              fontSize: "0.7rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <Box
                              sx={{
                                width: 4,
                                height: 4,
                                borderRadius: "50%",
                                background: "#12903C",
                              }}
                            />
                            {locationLabel}
                          </Typography>
                          <Box sx={{ ml: 1.75 }}>
                            <Typography
                              variant="body1"
                              color="text.primary"
                              sx={{
                                fontSize: { xs: "0.85rem", sm: "0.9rem" },
                                lineHeight: 1.5,
                                fontWeight: 500,
                                mb: project.mile_radius ? 0.5 : 0,
                              }}
                            >
                              {project.location.address_1
                                ? `${project.location.address_1}${project.location.address_2 ? `, ${project.location.address_2}` : ""}, ${project.location.city}, ${project.location.state}${project.location.zip ? ` ${project.location.zip}` : ""}`
                                : `${project.location.city || ""}${project.location.city && project.location.state ? ", " : ""}${project.location.state || ""}`}
                            </Typography>
                            {project.mile_radius && (
                              <Chip
                                label={`Within ${project.mile_radius} miles`}
                                size="small"
                                sx={{
                                  background: `linear-gradient(135deg, ${alpha("#12903C", 0.15)} 0%, ${alpha("#12903C", 0.1)} 100%)`,
                                  color: "#12903C",
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                  height: "22px",
                                  border: `1px solid ${alpha("#12903C", 0.2)}`,
                                  "&:hover": {
                                    background: `linear-gradient(135deg, ${alpha("#12903C", 0.2)} 0%, ${alpha("#12903C", 0.15)} 100%)`,
                                  },
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  {/* Chat Input Area - Fixed at Bottom */}
                  <Box
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      borderTop: `1px solid ${isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"}`,
                      background: isDark
                        ? `linear-gradient(180deg, ${alpha("#000000", 0.4)} 0%, ${alpha("#000000", 0.6)} 100%)`
                        : `linear-gradient(180deg, ${alpha("#ffffff", 0.95)} 0%, ${alpha("#ffffff", 1)} 100%)`,
                      backdropFilter: "blur(20px)",
                      flex: "0 0 auto",
                      overflow: "visible",
                      boxShadow: isDark
                        ? "0 -4px 16px rgba(0, 0, 0, 0.2)"
                        : "0 -4px 16px rgba(0, 0, 0, 0.05)",
                      marginTop: "auto",
                      position: "relative",
                    }}
                  >
                    <Box sx={{ position: "relative" }}>
                      <StyledTextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={3}
                        placeholder={chatboxPlaceholder}
                        value={resumeSearchQuery}
                        onChange={(e) => setResumeSearchQuery(e.target.value)}
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                            e.preventDefault();
                            if (resumeSearchQuery.trim()) {
                              handleResumeSearch();
                            }
                          }
                        }}
                        InputProps={{
                          endAdornment: resumeSearchQuery && (
                            <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => setResumeSearchQuery("")}
                                edge="end"
                                sx={{
                                  color: "text.secondary",
                                  "&:hover": {
                                    color: "text.primary",
                                    background: alpha("#12903C", 0.1),
                                  },
                                }}
                              >
                                <Clear fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            background: isDark
                              ? `linear-gradient(135deg, ${alpha("#ffffff", 0.12)} 0%, ${alpha("#ffffff", 0.08)} 100%)`
                              : `linear-gradient(135deg, ${alpha("#ffffff", 1)} 0%, ${alpha("#ffffff", 0.95)} 100%)`,
                            backdropFilter: "blur(40px) saturate(180%)",
                            WebkitBackdropFilter: "blur(40px) saturate(180%)",
                            border: `2px solid ${isDark ? alpha("#ffffff", 0.2) : alpha("#12903C", 0.2)}`,
                            boxShadow: isDark
                              ? "0 4px 16px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                              : "0 4px 16px rgba(18, 144, 60, 0.1), inset 0 1px 0 rgba(255, 255, 255, 1)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              border: `2px solid ${isDark ? alpha("#ffffff", 0.3) : alpha("#12903C", 0.3)}`,
                              boxShadow: isDark
                                ? "0 6px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                                : "0 6px 20px rgba(18, 144, 60, 0.15), inset 0 1px 0 rgba(255, 255, 255, 1)",
                            },
                            "&.Mui-focused": {
                              border: `2px solid ${isDark ? "#12903C" : "#12903C"}`,
                              boxShadow: isDark
                                ? "0 8px 24px rgba(18, 144, 60, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                                : "0 8px 24px rgba(18, 144, 60, 0.2), inset 0 1px 0 rgba(255, 255, 255, 1)",
                            },
                            "& .MuiInputBase-input": {
                              fontSize: "0.875rem",
                              lineHeight: 1.6,
                              padding: "12px 16px",
                              paddingRight: "110px",
                              overflow: "hidden",
                              fontWeight: 400,
                            },
                            "& .MuiInputBase-inputMultiline": {
                              paddingRight: "110px",
                              paddingTop: "12px",
                              paddingBottom: "12px",
                              overflow: "hidden",
                            },
                          },
                        }}
                      />
                      {/* Search Button - Inside Chatbox at Bottom Right */}
                      <Button
                        variant="contained"
                        onClick={handleResumeSearch}
                        disabled={isSearchingResume || !resumeSearchQuery.trim()}
                        startIcon={<Search />}
                        size="small"
                        sx={{
                          position: "absolute",
                          bottom: 35,
                          right: 8,
                          borderRadius: "8px",
                          textTransform: "none",
                          fontWeight: 600,
                          px: 1.5,
                          py: 0.5,
                          fontSize: "0.75rem",
                          minWidth: "auto",
                          background: `linear-gradient(135deg, #12903C 0%, #0d6e2f 100%)`,
                          boxShadow: "0 2px 8px rgba(18, 144, 60, 0.3)",
                          transition: "all 0.3s ease",
                          zIndex: 10,
                          "&:hover": {
                            background: `linear-gradient(135deg, #0d6e2f 0%, #12903C 100%)`,
                            boxShadow: "0 4px 12px rgba(18, 144, 60, 0.4)",
                            transform: "translateY(-1px)",
                          },
                          "&:active": {
                            transform: "translateY(0)",
                          },
                          "&:disabled": {
                            background: isDark ? alpha("#ffffff", 0.1) : alpha("#000000", 0.1),
                            boxShadow: "none",
                          },
                        }}
                      >
                        {isSearchingResume ? "..." : "Search"}
                      </Button>
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.5,
                          ml: 1,
                          display: "block",
                          color: "text.secondary",
                          fontSize: "0.7rem",
                          fontWeight: 400,
                        }}
                      >
                        Press <strong>Enter</strong> to search • <strong>Shift+Enter</strong> for new line
                      </Typography>
                    </Box>
                  </Box>

                </Box>
              )}
            </Box>
          )}
        </Box>
      </BaseModal>
      <ModalTalentDetail
        open={talentModal.visible}
        onClose={() => {
          setTalentModal({ visible: false, talent: null });
          setSelectedTalentId(null);
          setPendingTalentRow(null); // Clear any pending fallback data
        }}
        talent={talentModal.talent}
        project={project}
      />

      {/* Resume Detail Modal */}
      <BaseModal
        open={resumeDetailModal.visible}
        onClose={() => setResumeDetailModal({ visible: false, data: null })}
        title={`${resumeDetailModal.data?.parsed_data ? "Resume" : "Candidate"} Details - ${resumeDetailModal.data?.full_name || resumeDetailModal.data?.user?.name || "Candidate"}`}
        footer={
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => setResumeDetailModal({ visible: false, data: null })} variant="contained">
              Close
            </Button>
          </Stack>
        }
        maxWidth="lg"
        fullWidth={true}
        useGlassCard={true}
      >
        {resumeDetailModal.data && (
          <Box sx={{ p: 3, maxHeight: "70vh", overflow: "auto" }}>
            {/* Candidate Info Header */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: 2,
                background: isDark
                  ? `linear-gradient(135deg, ${alpha("#ffffff", 0.1)} 0%, ${alpha("#ffffff", 0.05)} 100%)`
                  : `linear-gradient(135deg, ${alpha("#12903C", 0.08)} 0%, ${alpha("#12903C", 0.03)} 100%)`,
                border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(18, 144, 60, 0.2)"}`,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, color: "#12903C", fontWeight: 700 }}>
                    {resumeDetailModal.data.full_name || resumeDetailModal.data.user?.name || "Candidate"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {resumeDetailModal.data.email || resumeDetailModal.data.user?.email || "N/A"}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {/* Availability Badge */}
                  {resumeDetailModal.data.matching_score?.is_available !== undefined && (
                    <Chip
                      label={resumeDetailModal.data.matching_score.is_available ? "Available" : "Currently Hired"}
                      color={resumeDetailModal.data.matching_score.is_available ? "success" : "error"}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                  {/* Work Status */}
                  {resumeDetailModal.data.user?.work_status && (
                    <Chip
                      label={resumeDetailModal.data.user.work_status === "sole-proprietor"
                        ? "Sole Proprietor"
                        : resumeDetailModal.data.user.work_status === "entrepreneur"
                          ? "Entrepreneur"
                          : resumeDetailModal.data.user.work_status === "part-time"
                            ? "Part Time"
                            : resumeDetailModal.data.user.work_status}
                      variant="outlined"
                      size="small"
                      sx={{
                        fontWeight: 500,
                        borderColor: "#12903C",
                        color: "#12903C"
                      }}
                    />
                  )}
                </Stack>
              </Stack>
            </Paper>

            {/* Matching Score Details */}
            {(resumeDetailModal.data.matching_score || resumeDetailModal.data.similarity_score != null) && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 2,
                  background: isDark
                    ? `linear-gradient(135deg, ${alpha("#ffffff", 0.1)} 0%, ${alpha("#ffffff", 0.05)} 100%)`
                    : `linear-gradient(135deg, ${alpha("#12903C", 0.08)} 0%, ${alpha("#12903C", 0.03)} 100%)`,
                  border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(18, 144, 60, 0.2)"}`,
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, color: "#12903C", fontWeight: 700 }}>
                  Matching Score
                </Typography>
                {resumeDetailModal.data.matching_score ? (
                  <Stack spacing={2}>
                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Match Score
                        </Typography>
                        <Chip
                          label={`${(resumeDetailModal.data.matching_score.total_score * 100).toFixed(1)}%`}
                          color={resumeDetailModal.data.matching_score.total_score >= 0.7 ? "success" : resumeDetailModal.data.matching_score.total_score >= 0.5 ? "warning" : "default"}
                          sx={{ fontWeight: 700, fontSize: "1rem" }}
                        />
                      </Stack>
                      <Box
                        sx={{
                          width: "100%",
                          height: 8,
                          borderRadius: 1,
                          background: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${resumeDetailModal.data.matching_score.total_score * 100}%`,
                            height: "100%",
                            background: resumeDetailModal.data.matching_score.total_score >= 0.7
                              ? "#12903C"
                              : resumeDetailModal.data.matching_score.total_score >= 0.5
                                ? "#ff9800"
                                : "#757575",
                            transition: "width 0.3s ease",
                          }}
                        />
                      </Box>
                    </Box>
                    <Divider />
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Similarity Score
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {((resumeDetailModal.data.matching_score.similarity_score || 0) * 100).toFixed(1)}%
                        </Typography>
                      </Stack>
                      {resumeDetailModal.data.matching_score.skill_match_score != null && (
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            Skill Match Score
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {(resumeDetailModal.data.matching_score.skill_match_score * 100).toFixed(1)}%
                          </Typography>
                        </Stack>
                      )}
                      {resumeDetailModal.data.matching_score.years_experience_score != null && (
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            Years Experience Score
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {(resumeDetailModal.data.matching_score.years_experience_score * 100).toFixed(1)}%
                          </Typography>
                        </Stack>
                      )}
                      {resumeDetailModal.data.matching_score.distance_km != null && (
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            Distance
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: resumeDetailModal.data.matching_score.distance_km < 50 ? "#12903C" : resumeDetailModal.data.matching_score.distance_km < 200 ? "#ff9800" : "#f44336" }}>
                            {(resumeDetailModal.data.matching_score.distance_km * 0.621371).toFixed(1)} miles
                          </Typography>
                        </Stack>
                      )}
                      {/* Note: availability_score is for ranking calculation only, not displayed */}
                      {resumeDetailModal.data.matching_score.is_available !== undefined && (
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            Availability Status
                          </Typography>
                          <Chip
                            label={resumeDetailModal.data.matching_score.is_available ? "Available" : "Currently Hired"}
                            color={resumeDetailModal.data.matching_score.is_available ? "success" : "error"}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </Stack>
                      )}
                      {resumeDetailModal.data.user?.work_status && (
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            Employment Type
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {resumeDetailModal.data.user.work_status === "sole-proprietor"
                              ? "Sole Proprietor"
                              : resumeDetailModal.data.user.work_status === "entrepreneur"
                                ? "Entrepreneur"
                                : resumeDetailModal.data.user.work_status === "part-time"
                                  ? "Part Time"
                                  : resumeDetailModal.data.user.work_status}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                ) : (
                  <Chip
                    label={`Match Score: ${(Number(resumeDetailModal.data.similarity_score) * 100).toFixed(1)}%`}
                    color={Number(resumeDetailModal.data.similarity_score) >= 0.7 ? "success" : Number(resumeDetailModal.data.similarity_score) >= 0.5 ? "warning" : "default"}
                    sx={{ fontWeight: 600, fontSize: "0.95rem", py: 2.5, px: 1 }}
                  />
                )}
              </Paper>
            )}

            {resumeDetailModal.data.parsed_data && (
              <>
                {/* Summary */}
                {resumeDetailModal.data.parsed_data.summary && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 3,
                      borderRadius: 2,
                      background: isDark
                        ? `linear-gradient(135deg, ${alpha("#ffffff", 0.1)} 0%, ${alpha("#ffffff", 0.05)} 100%)`
                        : `linear-gradient(135deg, ${alpha("#12903C", 0.08)} 0%, ${alpha("#12903C", 0.03)} 100%)`,
                      border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(18, 144, 60, 0.2)"}`,
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1.5, color: "#12903C", fontWeight: 700 }}>
                      Summary
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-line", lineHeight: 1.8 }}>
                      {resumeDetailModal.data.parsed_data.summary}
                    </Typography>
                  </Paper>
                )}

                {/* Position & Years Experience */}
                <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                  {resumeDetailModal.data.parsed_data.position && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        flex: 1,
                        borderRadius: 2,
                        background: isDark
                          ? `linear-gradient(135deg, ${alpha("#ffffff", 0.1)} 0%, ${alpha("#ffffff", 0.05)} 100%)`
                          : `linear-gradient(135deg, ${alpha("#12903C", 0.08)} 0%, ${alpha("#12903C", 0.03)} 100%)`,
                        border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(18, 144, 60, 0.2)"}`,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Position
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {resumeDetailModal.data.parsed_data.position}
                      </Typography>
                    </Paper>
                  )}
                  {resumeDetailModal.data.parsed_data.years_experience != null && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        flex: 1,
                        borderRadius: 2,
                        background: isDark
                          ? `linear-gradient(135deg, ${alpha("#ffffff", 0.1)} 0%, ${alpha("#ffffff", 0.05)} 100%)`
                          : `linear-gradient(135deg, ${alpha("#12903C", 0.08)} 0%, ${alpha("#12903C", 0.03)} 100%)`,
                        border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(18, 144, 60, 0.2)"}`,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Years of Experience
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {resumeDetailModal.data.parsed_data.years_experience} years
                      </Typography>
                    </Paper>
                  )}
                </Stack>

                {/* Skills */}
                {resumeDetailModal.data.parsed_data.skills && resumeDetailModal.data.parsed_data.skills.length > 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 3,
                      borderRadius: 2,
                      background: isDark
                        ? `linear-gradient(135deg, ${alpha("#ffffff", 0.1)} 0%, ${alpha("#ffffff", 0.05)} 100%)`
                        : `linear-gradient(135deg, ${alpha("#12903C", 0.08)} 0%, ${alpha("#12903C", 0.03)} 100%)`,
                      border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(18, 144, 60, 0.2)"}`,
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1.5, color: "#12903C", fontWeight: 700 }}>
                      Skills ({resumeDetailModal.data.parsed_data.skills.length})
                    </Typography>

                    {/* Match parsed skills with project skills */}
                    {(() => {
                      const parsedSkills = resumeDetailModal.data.parsed_data.skills || [];
                      const projectSkills = project.skills || [];

                      // If project has no skills, show all parsed skills as unmatched
                      if (!projectSkills || projectSkills.length === 0) {
                        return (
                          <Stack spacing={2}>
                            <Typography variant="body2" sx={{ mb: 1, color: "text.secondary", fontStyle: "italic" }}>
                              This project has no required skills.
                            </Typography>
                            <Box>
                              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}>
                                All Skills ({parsedSkills.length})
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {parsedSkills.map((skill: string, idx: number) => (
                                  <Chip
                                    key={idx}
                                    label={skill}
                                    size="small"
                                    sx={{
                                      mb: 1,
                                      background: isDark
                                        ? alpha("#ffffff", 0.1)
                                        : alpha("#000000", 0.05),
                                      color: "text.secondary",
                                      fontWeight: 400,
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          </Stack>
                        );
                      }

                      const projectSkillIds = projectSkills.map((s: any) => s.id) || [];

                      // Normalize skill name for better matching (remove dots, dashes, spaces, etc)
                      const normalizeSkillName = (name: string): string => {
                        return name
                          .toLowerCase()
                          .replace(/[\.\-\s]/g, "") // Remove dots, dashes, spaces
                          .trim();
                      };

                      // Extract base name (remove extensions like .js, .ts, etc)
                      const getBaseName = (name: string): string => {
                        const lower = name.toLowerCase().trim();
                        // Remove common extensions
                        return lower.replace(/\.(js|ts|jsx|tsx|vue|py|java|php|rb|go|rs)$/, "");
                      };

                      // Find matched skills from project requirements
                      const matchedSkills = parsedSkills.map((parsedSkill: string) => {
                        const parsedLower = parsedSkill.toLowerCase().trim();
                        const parsedNormalized = normalizeSkillName(parsedSkill);
                        const parsedBase = getBaseName(parsedSkill);

                        // Try exact match first (normalized)
                        let matched = projectSkills.find((projectSkill: any) => {
                          if (!projectSkill || !projectSkill.name) return false;
                          const projectNormalized = normalizeSkillName(projectSkill.name);
                          return parsedNormalized === projectNormalized;
                        });

                        // If no exact match, try case-insensitive exact match
                        if (!matched) {
                          matched = projectSkills.find((projectSkill: any) => {
                            if (!projectSkill || !projectSkill.name) return false;
                            return parsedLower === projectSkill.name.toLowerCase().trim();
                          });
                        }

                        // Try base name matching (e.g., "React.js" matches "React")
                        if (!matched) {
                          matched = projectSkills.find((projectSkill: any) => {
                            if (!projectSkill || !projectSkill.name) return false;
                            const projectLower = projectSkill.name.toLowerCase().trim();
                            const projectBase = getBaseName(projectSkill.name);
                            // Match if base names are the same
                            if (parsedBase === projectBase && parsedBase.length >= 2) return true;
                            // Or if one base name is contained in the other
                            if (parsedBase.length >= 3 && projectBase.length >= 3) {
                              return parsedBase.includes(projectBase) || projectBase.includes(parsedBase);
                            }
                            return false;
                          });
                        }

                        // If no exact match, try partial match (one contains the other)
                        if (!matched) {
                          matched = projectSkills.find((projectSkill: any) => {
                            if (!projectSkill || !projectSkill.name) return false;
                            const projectLower = projectSkill.name.toLowerCase().trim();
                            // Check if one contains the other (but require at least 3 characters match)
                            if (parsedLower.length >= 3 && projectLower.length >= 3) {
                              return parsedLower.includes(projectLower) || projectLower.includes(parsedLower);
                            }
                            return false;
                          });
                        }

                        // If still no match, try word-by-word matching for multi-word skills
                        if (!matched && parsedSkill.includes(" ")) {
                          const parsedWords = parsedSkill.toLowerCase().split(/\s+/);
                          matched = projectSkills.find((projectSkill: any) => {
                            if (!projectSkill || !projectSkill.name) return false;
                            const projectLower = projectSkill.name.toLowerCase();
                            // Check if all significant words (length >= 3) in parsed skill appear in project skill name
                            const significantWords = parsedWords.filter((word: string) => word.length >= 3);
                            return significantWords.length > 0 && significantWords.every((word: string) => projectLower.includes(word));
                          });
                        }

                        return {
                          parsed: parsedSkill,
                          matched: matched,
                          isProjectSkill: !!matched,
                        };
                      });

                      const projectMatched = matchedSkills.filter((s: any) => s.isProjectSkill);
                      const unmatched = matchedSkills.filter((s: any) => !s.matched);

                      return (
                        <Stack spacing={2}>
                          {projectMatched.length > 0 && (
                            <Box>
                              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#12903C" }}>
                                Matched with Project Requirements ({projectMatched.length})
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {projectMatched.map((item: any, idx: number) => (
                                  <Chip
                                    key={idx}
                                    label={item.matched?.name || item.parsed}
                                    size="small"
                                    sx={{
                                      mb: 1,
                                      background: "#12903C",
                                      color: "#ffffff",
                                      fontWeight: 600,
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          )}

                          {unmatched.length > 0 && (
                            <Box>
                              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}>
                                Not Matched ({unmatched.length})
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {unmatched.map((item: any, idx: number) => (
                                  <Chip
                                    key={idx}
                                    label={item.parsed}
                                    size="small"
                                    sx={{
                                      mb: 1,
                                      background: isDark
                                        ? alpha("#ffffff", 0.1)
                                        : alpha("#000000", 0.05),
                                      color: "text.secondary",
                                      fontWeight: 400,
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      );
                    })()}
                  </Paper>
                )}

                {/* Education */}
                {resumeDetailModal.data.parsed_data.education && resumeDetailModal.data.parsed_data.education.length > 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 3,
                      borderRadius: 2,
                      background: isDark
                        ? `linear-gradient(135deg, ${alpha("#ffffff", 0.1)} 0%, ${alpha("#ffffff", 0.05)} 100%)`
                        : `linear-gradient(135deg, ${alpha("#12903C", 0.08)} 0%, ${alpha("#12903C", 0.03)} 100%)`,
                      border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(18, 144, 60, 0.2)"}`,
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1.5, color: "#12903C", fontWeight: 700 }}>
                      Education
                    </Typography>
                    <Stack spacing={2}>
                      {resumeDetailModal.data.parsed_data.education.map((edu: any, idx: number) => (
                        <Box key={idx}>
                          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {edu.field || "N/A"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {edu.degree && `${edu.degree}, `}
                            {edu.institution}
                            {edu.graduation_year && ` • ${edu.graduation_year}`}
                          </Typography>
                          {idx < resumeDetailModal.data.parsed_data.education.length - 1 && (
                            <Divider sx={{ mt: 2 }} />
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                )}

                {/* Experience */}
                {resumeDetailModal.data.parsed_data.experience && resumeDetailModal.data.parsed_data.experience.length > 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 3,
                      borderRadius: 2,
                      background: isDark
                        ? `linear-gradient(135deg, ${alpha("#ffffff", 0.1)} 0%, ${alpha("#ffffff", 0.05)} 100%)`
                        : `linear-gradient(135deg, ${alpha("#12903C", 0.08)} 0%, ${alpha("#12903C", 0.03)} 100%)`,
                      border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(18, 144, 60, 0.2)"}`,
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1.5, color: "#12903C", fontWeight: 700 }}>
                      Work Experience
                    </Typography>
                    <Stack spacing={2.5}>
                      {resumeDetailModal.data.parsed_data.experience.map((exp: any, idx: number) => (
                        <Box key={idx}>
                          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {exp.position || "N/A"} {exp.position && exp.company && "•"} {exp.company || "N/A"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {exp.start_date && `${exp.start_date}`}
                            {exp.start_date && exp.end_date && " - "}
                            {exp.end_date || (exp.start_date && "Present")}
                          </Typography>
                          {exp.description && (
                            <Typography variant="body2" sx={{ whiteSpace: "pre-line", lineHeight: 1.8 }}>
                              {exp.description}
                            </Typography>
                          )}
                          {idx < resumeDetailModal.data.parsed_data.experience.length - 1 && (
                            <Divider sx={{ mt: 2 }} />
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                )}

                {/* Certifications */}
                {resumeDetailModal.data.parsed_data.certifications && resumeDetailModal.data.parsed_data.certifications.length > 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 3,
                      borderRadius: 2,
                      background: isDark
                        ? `linear-gradient(135deg, ${alpha("#ffffff", 0.1)} 0%, ${alpha("#ffffff", 0.05)} 100%)`
                        : `linear-gradient(135deg, ${alpha("#12903C", 0.08)} 0%, ${alpha("#12903C", 0.03)} 100%)`,
                      border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(18, 144, 60, 0.2)"}`,
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1.5, color: "#12903C", fontWeight: 700 }}>
                      Certifications
                    </Typography>
                    <Stack spacing={1}>
                      {resumeDetailModal.data.parsed_data.certifications.map((cert: any, idx: number) => (
                        <Typography key={idx} variant="body2">
                          {typeof cert === "string" ? cert : cert.name || JSON.stringify(cert)}
                        </Typography>
                      ))}
                    </Stack>
                  </Paper>
                )}
              </>
            )}

            {/* Skills from user.skills (when parsed_data is null) */}
            {!resumeDetailModal.data.parsed_data && resumeDetailModal.data.user?.skills && resumeDetailModal.data.user.skills.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 2,
                  background: isDark
                    ? `linear-gradient(135deg, ${alpha("#ffffff", 0.1)} 0%, ${alpha("#ffffff", 0.05)} 100%)`
                    : `linear-gradient(135deg, ${alpha("#12903C", 0.08)} 0%, ${alpha("#12903C", 0.03)} 100%)`,
                  border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(18, 144, 60, 0.2)"}`,
                }}
              >
                <Typography variant="h6" sx={{ mb: 1.5, color: "#12903C", fontWeight: 700 }}>
                  Skills ({resumeDetailModal.data.user.skills.length})
                </Typography>
                {(() => {
                  const userSkills = resumeDetailModal.data.user.skills || [];
                  const projectSkills = project.skills || [];

                  // If project has no skills, show all user skills
                  if (!projectSkills || projectSkills.length === 0) {
                    return (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {userSkills.map((skill: any) => (
                          <Chip
                            key={skill.id}
                            label={skill.name}
                            size="small"
                            sx={{
                              mb: 1,
                              background: isDark
                                ? alpha("#ffffff", 0.1)
                                : alpha("#000000", 0.05),
                              color: "text.secondary",
                              fontWeight: 400,
                            }}
                          />
                        ))}
                      </Stack>
                    );
                  }

                  // Normalize skill name for better matching
                  const normalizeSkillName = (name: string): string => {
                    return name
                      .toLowerCase()
                      .replace(/[\.\-\s]/g, "")
                      .trim();
                  };

                  // Extract base name (remove extensions like .js, .ts, etc)
                  const getBaseName = (name: string): string => {
                    const lower = name.toLowerCase().trim();
                    return lower.replace(/\.(js|ts|jsx|tsx|vue|py|java|php|rb|go|rs)$/, "");
                  };

                  // Match user skills with project skills by name (case-insensitive, partial match)
                  const matchedSkills = userSkills.filter((userSkill: any) => {
                    if (!userSkill || !userSkill.name) return false;
                    const userSkillName = userSkill.name;
                    const userLower = userSkillName.toLowerCase().trim();
                    const userNormalized = normalizeSkillName(userSkillName);
                    const userBase = getBaseName(userSkillName);

                    return projectSkills.some((projectSkill: any) => {
                      if (!projectSkill || !projectSkill.name) return false;

                      // First try ID match (exact match)
                      if (projectSkill.id === userSkill.id) return true;

                      const projectLower = projectSkill.name.toLowerCase().trim();
                      const projectNormalized = normalizeSkillName(projectSkill.name);
                      const projectBase = getBaseName(projectSkill.name);

                      // Try exact match first (normalized)
                      if (userNormalized === projectNormalized) return true;

                      // Try case-insensitive exact match
                      if (userLower === projectLower) return true;

                      // Try base name matching (e.g., "React.js" matches "React")
                      if (userBase === projectBase && userBase.length >= 2) return true;
                      if (userBase.length >= 3 && projectBase.length >= 3) {
                        if (userBase.includes(projectBase) || projectBase.includes(userBase)) return true;
                      }

                      // Try partial match (one contains the other)
                      if (userLower.length >= 3 && projectLower.length >= 3) {
                        if (userLower.includes(projectLower) || projectLower.includes(userLower)) return true;
                      }

                      // Try word-by-word matching for multi-word skills
                      if (userSkillName.includes(" ")) {
                        const userWords = userSkillName.toLowerCase().split(/\s+/);
                        const significantWords = userWords.filter((word: string) => word.length >= 3);
                        if (significantWords.length > 0 && significantWords.every((word: string) => projectLower.includes(word))) return true;
                      }

                      return false;
                    });
                  });

                  const unmatchedSkills = userSkills.filter((userSkill: any) => {
                    if (!userSkill || !userSkill.name) return true;
                    const userSkillName = userSkill.name;
                    const userLower = userSkillName.toLowerCase().trim();
                    const userNormalized = normalizeSkillName(userSkillName);
                    const userBase = getBaseName(userSkillName);

                    return !projectSkills.some((projectSkill: any) => {
                      if (!projectSkill || !projectSkill.name) return false;

                      // First try ID match
                      if (projectSkill.id === userSkill.id) return true;

                      const projectLower = projectSkill.name.toLowerCase().trim();
                      const projectNormalized = normalizeSkillName(projectSkill.name);
                      const projectBase = getBaseName(projectSkill.name);

                      // Try all matching methods
                      if (userNormalized === projectNormalized) return true;
                      if (userLower === projectLower) return true;
                      if (userBase === projectBase && userBase.length >= 2) return true;
                      if (userBase.length >= 3 && projectBase.length >= 3) {
                        if (userBase.includes(projectBase) || projectBase.includes(userBase)) return true;
                      }
                      if (userLower.length >= 3 && projectLower.length >= 3) {
                        if (userLower.includes(projectLower) || projectLower.includes(userLower)) return true;
                      }
                      if (userSkillName.includes(" ")) {
                        const userWords = userSkillName.toLowerCase().split(/\s+/);
                        const significantWords = userWords.filter((word: string) => word.length >= 3);
                        if (significantWords.length > 0 && significantWords.every((word: string) => projectLower.includes(word))) return true;
                      }

                      return false;
                    });
                  });

                  return (
                    <Stack spacing={2}>
                      {matchedSkills.length > 0 && (
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#12903C" }}>
                            Matched with Project Requirements ({matchedSkills.length})
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {matchedSkills.map((skill: any) => (
                              <Chip
                                key={skill.id}
                                label={skill.name}
                                size="small"
                                sx={{
                                  mb: 1,
                                  background: "#12903C",
                                  color: "#ffffff",
                                  fontWeight: 600,
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                      {unmatchedSkills.length > 0 && (
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}>
                            Other Skills ({unmatchedSkills.length})
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {unmatchedSkills.map((skill: any) => (
                              <Chip
                                key={skill.id}
                                label={skill.name}
                                size="small"
                                sx={{
                                  mb: 1,
                                  background: isDark
                                    ? alpha("#ffffff", 0.1)
                                    : alpha("#000000", 0.05),
                                  color: "text.secondary",
                                  fontWeight: 400,
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  );
                })()}
              </Paper>
            )}

            {/* No data message */}
            {!resumeDetailModal.data.parsed_data && (!resumeDetailModal.data.user?.skills || resumeDetailModal.data.user.skills.length === 0) && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 2,
                  background: isDark
                    ? `linear-gradient(135deg, ${alpha("#ffffff", 0.1)} 0%, ${alpha("#ffffff", 0.05)} 100%)`
                    : `linear-gradient(135deg, ${alpha("#12903C", 0.08)} 0%, ${alpha("#12903C", 0.03)} 100%)`,
                  border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(18, 144, 60, 0.2)"}`,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", fontStyle: "italic" }}>
                  No resume data or skills available for this candidate.
                </Typography>
              </Paper>
            )}
          </Box>
        )}
      </BaseModal>
    </>
  );
};
export default ModalInvite;

